import { 
  SlideCanvasPoint, 
  SlideCanvasBox 
} from '../components/SlideCanvas.types';
import { normalizeRotation } from './slideRotation';

/**
 * Logic: Rotate Action (Canvas Space)
 * 
 * Centralizes the angle calculation and state management for the rotation gesture.
 */

export interface RotationState {
  centerCanvas: SlideCanvasPoint;
  startAngle: number;
  originalRotation: number;
}

/**
 * Initializes the rotation session. Calculates the angle between box center
 * and the initial click point.
 */
export function initializeRotation(
  box: SlideCanvasBox,
  startPointCanvas: SlideCanvasPoint,
  scaleX: number,
  scaleY: number
): RotationState {
  const centerCanvas = {
    x: (box.x + box.width / 2) * scaleX,
    y: (box.y + box.height / 2) * scaleY,
  };

  // Math: atan2 calculates the angle to the start point in radians.
  // This is used as the baseline 'zero' angle for the current session.
  const startAngle = Math.atan2(
    startPointCanvas.y - centerCanvas.y,
    startPointCanvas.x - centerCanvas.x
  );

  return {
    centerCanvas,
    startAngle,
    originalRotation: box.rotation
  };
}

/**
 * Applies the rotation by computing the delta angle between start and current pointer.
 */
export function applyRotation(
  currentPointCanvas: SlideCanvasPoint,
  state: RotationState
): number {
  // 1. Calculate the current angle from the center to the cursor in radians.
  const currentAngle = Math.atan2(
    currentPointCanvas.y - state.centerCanvas.y,
    currentPointCanvas.x - state.centerCanvas.x
  );

  // 2. Compute the delta rotation and add to the original value.
  // The radians difference is converted to degrees (180 / PI).
  const delta = (currentAngle - state.startAngle) * (180 / Math.PI);
  return normalizeRotation(state.originalRotation + delta);
}
